import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import Select, { components } from "react-select";
import CreatableSelect from "react-select/creatable";
import { Form } from "react-bootstrap";
import { KInputHintVariant, noop, tooltipate } from "../index";
import { stringify } from "./commons";
export var KOperation;
(function (KOperation) {
    KOperation["ADDED"] = "added";
    KOperation["DELETED"] = "deleted";
})(KOperation || (KOperation = {}));
export var KSELECT_SIZE;
(function (KSELECT_SIZE) {
    KSELECT_SIZE[KSELECT_SIZE["sm"] = 27] = "sm";
    KSELECT_SIZE[KSELECT_SIZE["nm"] = 30] = "nm";
    KSELECT_SIZE[KSELECT_SIZE["lg"] = 40] = "lg";
})(KSELECT_SIZE || (KSELECT_SIZE = {}));
export var KSELECT_BACKCOLOR;
(function (KSELECT_BACKCOLOR) {
    KSELECT_BACKCOLOR["clear"] = "#FFF";
    KSELECT_BACKCOLOR["gray"] = "#f6f6f7";
})(KSELECT_BACKCOLOR || (KSELECT_BACKCOLOR = {}));
const fontSizeMap = {
    [KSELECT_SIZE.sm]: 11,
    [KSELECT_SIZE.nm]: 14,
    [KSELECT_SIZE.lg]: 14,
};
export function KSelect({ id, name, value, clearable = false, searchable = false, placeholder, defaultValue, multi = false, onChange, disabled = false, required = false, options, label, errorMessage, minWidth = 170, underline = false, size = KSELECT_SIZE.nm, backColor = KSELECT_BACKCOLOR.clear, hint, hintVariant = KInputHintVariant.DEFAULT, tooltip, creatable = false, equal = (a, b) => a === b, }) {
    var _a, _b;
    const [stateValue, setStateValue] = useState(value || defaultValue);
    useEffect(() => {
        if (value && stateValue !== value) {
            setStateValue(value);
        }
    }, [value, stateValue, setStateValue]);
    const selectRef = useRef(null);
    const optionsMap = options.reduce((previousValue, currentValue) => {
        return previousValue.set(currentValue.value, currentValue);
    }, new Map());
    const fontBase = {
        fontFamily: "KTextFont, sans-serif",
        fontSize: fontSizeMap[size],
        letterSpacing: 0,
    };
    const DropdownIndicator = (props) => (_jsx(components.DropdownIndicator, { ...props, children: props.isFocused ? _jsx("i", { className: "fa fa-caret-up" }) : _jsx("i", { className: "fa fa-caret-down" }) }));
    const ClearIndicator = (props) => (_jsx(components.ClearIndicator, { ...props, children: _jsx("i", { className: "fal fa-times" }) }));
    function createUpdated(is, will) {
        const result = [];
        for (const e of is) {
            if (!will.some((v) => equal(e, v))) {
                result.push({ value: e, operation: KOperation.DELETED });
            }
        }
        for (const e of will) {
            if (!is.some((v) => equal(e, v))) {
                result.push({ value: e, operation: KOperation.ADDED });
            }
        }
        return result;
    }
    const compProps = {
        id: `${id}-select`,
        options,
        defaultValue: defaultValue !== undefined
            ? Array.isArray(defaultValue)
                ? defaultValue.map((v) => { var _a; return (_a = optionsMap.get(v)) !== null && _a !== void 0 ? _a : { value: v, label: stringify(v) || "" }; })
                : (_a = optionsMap.get(defaultValue)) !== null && _a !== void 0 ? _a : { value: defaultValue, label: stringify(defaultValue) || "" }
            : undefined,
        value: stateValue !== undefined
            ? Array.isArray(stateValue)
                ? stateValue.map((v) => { var _a; return (_a = optionsMap.get(v)) !== null && _a !== void 0 ? _a : { value: v, label: stringify(v) || "" }; })
                : (_b = optionsMap.get(stateValue)) !== null && _b !== void 0 ? _b : { value: stateValue, label: stringify(stateValue) || "" }
            : multi
                ? []
                : null,
        isMulti: multi,
        isDisabled: disabled,
        isSearchable: searchable,
        isClearable: clearable,
        placeholder,
        components: { DropdownIndicator, ClearIndicator },
        filterOption: (option, searchStr) => (option.data.searchText || option.label || option.value).toString().toUpperCase().indexOf(searchStr.toUpperCase()) !== -1,
        onChange: (selected) => {
            if (!Array.isArray(selected)) {
                const koValue = selected === null || selected === void 0 ? void 0 : selected.value;
                if (onChange) {
                    onChange(koValue);
                }
                setStateValue(koValue);
            }
            else {
                const koValues = selected === null || selected === void 0 ? void 0 : selected.map((o) => o.value);
                if (onChange) {
                    onChange(koValues, createUpdated(stateValue || [], koValues || []));
                }
                setStateValue(koValues);
            }
        },
        className: label ? "" : "no-label",
        classNamePrefix: "kselect",
        styles: {
            control: (styles, { isFocused }) => ({
                ...styles,
                marginTop: label ? "29px" : "0",
                borderRadius: 0,
                backgroundColor: backColor,
                border: underline ? "none" : undefined,
                borderColor: underline ? undefined : "#DFE7F5",
                boxShadow: "none",
                borderBottom: underline ? "2px solid #f6f6f7" : undefined,
                "&:hover": {
                    borderBottomColor: underline ? (isFocused ? "#5198f4" : "#f6f6f7") : undefined,
                    borderColor: underline ? undefined : "#DFE7F5",
                },
                borderBottomColor: underline ? (isFocused ? "#5198f4" : "#f6f6f7") : undefined,
                // height: size,
                minHeight: size,
                minWidth,
            }),
            option: (styles, { isDisabled, isFocused, isSelected }) => ({
                ...styles,
                ...fontBase,
                backgroundColor: isDisabled ? "#FFF" : isSelected ? "#5198f4" : isFocused ? "#DFE7F5" : "#FFF",
                color: isDisabled ? "#b5b7bf" : isSelected ? "#FFF" : "#4e596b",
                cursor: isDisabled ? "not-allowed" : "default",
                fontStyle: isDisabled ? "italic" : "normal",
            }),
            placeholder: (styles, {}) => ({
                ...styles,
                ...fontBase,
                display: "inline-flex",
            }),
            singleValue: (styles, {}) => ({
                ...styles,
                ...fontBase,
                display: "inline-flex",
            }),
            dropdownIndicator: (styles, {}) => ({
                ...styles,
                paddingTop: size === KSELECT_SIZE.lg ? 10 : 4,
                color: "#363D49",
            }),
            indicatorSeparator: () => ({
                display: "none",
            }),
            valueContainer: (styles, {}) => ({
                ...styles,
                flex: "1 1 0% !important",
                flexWrap: "wrap !important",
                // height: size,
            }),
            input: (styles) => ({
                ...styles,
                ...fontBase,
                color: "#4e596b",
                marginTop: 0,
            }),
            multiValue: (styles) => ({
                ...styles,
                ...fontBase,
                color: "#4e596b",
                // marginTop: searchable ? (size === KSELECT_SIZE.lg ? 2 : -2) : size === KSELECT_SIZE.lg ? 2 : -1,
            }),
            multiValueRemove: (styles) => ({
                ...styles,
                "&:hover": {
                    backgroundColor: "#ef615e",
                    color: "#FFF",
                },
            }),
            clearIndicator: (styles) => ({
                ...styles,
                paddingTop: size === KSELECT_SIZE.lg ? 11 : 5,
            }),
        },
    };
    return (_jsxs(Form.Group, { controlId: id, className: "k-input-group k-select-group", children: [_jsx(Form.Control, { name: name || id, type: "text", required: required, className: `inner-input`, onChange: () => noop(), onFocus: () => selectRef.current && selectRef.current.focus(), value: stringify(stateValue) }), creatable ? _jsx(CreatableSelect, { ...compProps, ref: selectRef }) : _jsx(Select, { ...compProps, ref: selectRef }), hint ? _jsx("div", { className: `k-hint k-hint-variant-${hintVariant}`, children: hint }) : null, label ? (_jsxs("div", { className: "label-holder", children: [tooltipate(_jsx(Form.Label, { className: `k-input-label${required ? " required" : ""}`, children: label }), tooltip, undefined, true), errorMessage ? (_jsx(Form.Control.Feedback, { as: "span", type: "invalid", children: errorMessage })) : null] })) : errorMessage ? (_jsx(Form.Control.Feedback, { as: "span", type: "invalid", children: errorMessage })) : null] }));
}
